import { formatPrice, isGroup } from '@/utils/listings'

import type { Listing } from './types'

export const getIsRented = (listing: Listing) => {
  return listing.state_machine === 'rented' || isTrue(listing.marked_as_rented)
}

export const isTrue = (value: string | number | boolean): boolean => {
  return value === '1' || value === 1 || value === true
}

export const renderAddress = ({
  street_name,
  hide_unit_number,
  full_street_name,
  building_type
}: Listing) => {
  return isTrue(hide_unit_number) && building_type === 'house'
    ? street_name
    : full_street_name
}

export const getPrice = (listing: Listing) =>
  isGroup(listing)
    ? {
        min: listing.gr_min_price?.toString(),
        max: listing.gr_max_price?.toString()
      }
    : listing?.price?.toString()

export const getNewPrice = (listing: Listing) =>
  isGroup(listing)
    ? {
        min: listing.gr_min_price?.toString(),
        max: listing.gr_max_price?.toString()
      }
    : { min: listing.price?.toString() }

export const formatPriceFromListing = (listing: Listing): string => {
  const price: string | { min: string | undefined; max: string | undefined } =
    getPrice(listing)

  if (isGroup(listing)) {
    const { min } = price as { min: string; max: string }
    const formattedMinPrice = formatPrice(min)
    return `${formattedMinPrice}+`
  } else return formatPrice(price)
}

export const getClickEventId = (
  listing: Listing,
  featured: boolean,
  featuredPosition: string
) => {
  // When user clicks a listing card and navigates to the listing details page
  // All possible listing card click event IDs:
  // listing-card-regular
  // listing-card-verified
  // listing-card-featured-#
  // blockapp-card-verified
  // blockapp-card-featured-#
  // mls-card-verified
  // mls-card-featured-#

  const isMLSListing = listing?.public_viewing === false
  const allowApplications = listing?.allow_applications
  const isVerified = listing?.verified_state_machine === 'approved'

  let cardType = 'listing'
  if (isMLSListing) {
    cardType = 'mls'
  } else if (!allowApplications) {
    cardType = 'blockapp'
  }

  let cardStatus = 'regular'
  if (featured) {
    cardStatus = 'featured'
    if (featuredPosition) {
      cardStatus += `-${featuredPosition}`
    }
  } else if (isVerified) {
    cardStatus = 'verified'
  }

  return `${cardType}-card-${cardStatus}`
}

export const getListingCardDlv = (listing: Listing, featured: boolean) => {
  // When user interacts with a listing card (click, apply, message, favorite)
  // All possible listing card DataLayer variables:
  // regular
  // verified
  // featured
  // blockapplication
  // blockapplicationfeatured
  // mls
  // mlsfeatured

  const isMLSListing = listing?.public_viewing === false
  const allowApplications = listing?.allow_applications
  const isVerified = listing?.verified_state_machine === 'approved'

  if (isMLSListing) {
    return featured ? 'mlsfeatured' : 'mls'
  } else if (!allowApplications) {
    return featured ? 'blockapplicationfeatured' : 'blockapplication'
  } else if (featured) {
    return 'featured'
  } else if (isVerified) {
    return 'verified'
  } else {
    return 'regular'
  }
}

export const setListingCardDataLayer = ({
  listing,
  featured,
  eventId
}: {
  listing: Listing
  featured: boolean
  eventId: string
}) => {
  window.dataLayer.push({
    event: eventId,
    listingCard: getListingCardDlv(listing, featured)
  })
}
